import Typography from '@material-ui/core/Typography';
import MenuSelector from '../MenuSelector';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import CancelButton from '../CancelButton';
import PrimaryButton from '../PrimaryButton';
import AddButton from '../AddButton';
import { globalStyles as styles } from '../style/gstheme';
import TitledPanel from '../TitledPanel';
import TitledTextField from '../TitledTextField';
import { MAX_DESCRIPTION_LENGTH } from '../utils/constants';
import ComponentSelector from '../../application-core/ComponentSelector';
import GS from '../../grafsync/grafsync';
import { lightSanitiseText, computeLabelFromType } from '../utils/utils';

const projectOptions = {
    "TaxonomyProject": [
        "http://www.w3.org/2004/02/skos/core#Collection",
        "http://www.w3.org/2004/02/skos/core#Concept",
        "http://www.w3.org/2004/02/skos/core#OrderedCollection",
        "http://www.w3.org/2004/02/skos/core#ConceptScheme",
        "http://www.w3.org/2008/05/skos-xl#Label",
        "https://graphifi.com/schema/accesscontrol/User",
        "http://www.w3.org/2001/XMLSchema#date",
        "http://www.w3.org/2001/XMLSchema#integer",
        "http://www.w3.org/2001/XMLSchema#decimal",
        "http://www.w3.org/2001/XMLSchema#string"
    ],
    "OntologyProject": [
        "http://www.w3.org/2002/07/owl#AnnotationProperty",
        "http://www.w3.org/2002/07/owl#DatatypeProperty",
        "http://www.w3.org/2002/07/owl#Class",
        "http://www.w3.org/2002/07/owl#ObjectProperty",
        "http://www.w3.org/2002/07/owl#Ontology",
        "http://www.w3.org/2002/07/owl#NamedIndividual",
        "https://graphifi.com/schema/accesscontrol/User"
    ],
    "DataProject": [
        "https://grafsync.graphifi.com/schema/gs/Dataset",
        "https://grafsync.graphifi.com/schema/gs/Resource",
        "https://graphifi.com/schema/accesscontrol/User",
        "http://www.w3.org/2001/XMLSchema#date",
        "http://www.w3.org/2001/XMLSchema#integer",
        "http://www.w3.org/2001/XMLSchema#decimal",
        "http://www.w3.org/2001/XMLSchema#string"
    ]
};

const constraintClasses = {
    "https://grafsync.graphifi.com/schema/gs/Resource": [
        "Dataset"
    ],
    "http://www.w3.org/2004/02/skos/core#Concept": [
        "Collection",
        "OrderedCollection",
        "ConceptScheme",
    ],
    "http://www.w3.org/2008/05/skos-xl#Label": [
        "ConceptScheme",
    ],
    "http://www.w3.org/2002/07/owl#AnnotationProperty": [
        "Ontology"
    ],
    "http://www.w3.org/2002/07/owl#DatatypeProperty": [
        "Ontology"
    ],
    "http://www.w3.org/2002/07/owl#Class": [
        "Ontology"
    ],
    "http://www.w3.org/2002/07/owl#ObjectProperty": [
        "Ontology"
    ],
    "http://www.w3.org/2002/07/owl#NamedIndividual": [
        "Ontology"
    ]
};

class AddParameterDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isValidIRI: true,
            open: false,
            expectedType: undefined,
            variable: undefined,
            title: "",
            description: "",
            component: undefined,
            isReport: false
        };
    }

    handleDescriptionChange = (event) => {
        const t = event.target;
        const cleanText = lightSanitiseText(t.value);
        this.setState({ [t.name]: cleanText });
    };

    handleClickOpen = () => this.setState({ open: true });

    addParameter = () => {
        const { variable, description, expectedType, component } = this.state;
        this.props.onAddParameter({
            expectedType,
            description,
            component,
            variable
        });
        this.exitAddParameter();
    };

    exitAddParameter = () => this.setState({
        open: false,
        description: "",
        expectedType: undefined,
        component: undefined,
        variable: undefined
    });

    handleVariableChange = (variable) => this.setState({ variable });

    handleTypeChange = (expectedType) => this.setState({ expectedType, component: undefined });

    handleComponentChange = (component) => this.setState({ component });

    render() {

        const { classes, defaultLang, queryVars, existing, projectType, graph, project, components } = this.props;
        const { description, open, isLoading, variable, expectedType, component } = this.state;

        if (!open) {
            return <AddButton dataTest="add-parameter" onClick={() => this.handleClickOpen()} title="Add a parameter" />
        }

        const allowedVars = existing && existing.length > 0 ?
            queryVars.filter(qv => !existing.some(ex => qv === "?" + ex))
            :
            queryVars;

        const options = allowedVars
            .map(av => ({
                value: av.substring(1),
                label: av.substring(1),
                disabled: !av.substring(1).match("^[A-Za-z_]+[0-9]*$")
            }));

        const isUsed = variable && !allowedVars.includes("?" + variable);
        const disabled = description === "" || isLoading || !variable || !expectedType || isUsed;

        const typeOptions = projectOptions[projectType] ?
            projectOptions[projectType].map(pt => {
                const alias = GS.getAlias(pt);
                const label = pt.startsWith("http://www.w3.org/2001/XMLSchema#") ?
                    "XSD " + pt.substring(33)
                    :
                    computeLabelFromType(alias);
                return {
                    value: pt,
                    label
                };
            })
            :
            [];

        const constrainedComponents = components && expectedType && constraintClasses[expectedType] ?
            components
                .filter(c => constraintClasses[expectedType].includes(c.type))
                .map(c => c.id)
            :
            [];

        return (
            <Dialog
                data-test="add-parameter-dialog"
                maxWidth="md"
                fullWidth
                classes={{
                    paper: classes.paper,
                    paperWidthMd: classes.mediumDialog
                }}
                className={classes.dialog}
                open={this.state.open}
                onClose={this.exitAddParameter}
                aria-labelledby="add-paramter-dialog-title">
                <DialogTitle disableTypography className={classes.mainHeading} id="add-parameter-dialog-title">Add Parameter</DialogTitle>
                <DialogContent>
                    <form autoComplete="off" noValidate="novalidate">
                        <TitledPanel margin title="Variable">
                            <MenuSelector
                                disabled={isLoading}
                                dataTest="variable-selector"
                                hintWord="variable"
                                selected={variable}
                                handleChange={this.handleVariableChange}
                                options={options} />
                            {isUsed && <Typography data-test="conflicting-variable" variant="caption">The chosen variable is no longer available to select.</Typography>}
                        </TitledPanel>
                        <TitledTextField
                            margin
                            dataTest="description"
                            name="description"
                            title="Description"
                            multiline={true}
                            required={true}
                            rows={2}
                            lang={defaultLang}
                            placeholder="Enter description for parameter"
                            text={description}
                            maxLength={MAX_DESCRIPTION_LENGTH}
                            onChange={this.handleDescriptionChange} />
                        <TitledPanel margin title="Expected Type">
                            <MenuSelector
                                disabled={isLoading}
                                dataTest="type-selector"
                                hintWord="type"
                                selected={expectedType}
                                handleChange={this.handleTypeChange}
                                options={typeOptions} />
                        </TitledPanel>
                        {expectedType && constraintClasses[expectedType] &&
                            <TitledPanel dataTest="constrain-type" margin title="Constrain Type Search By (Optional)">
                                <ComponentSelector
                                    id={project}
                                    key={expectedType}
                                    allTitle="Do not constrain"
                                    defaultLang={defaultLang}
                                    selected={component}
                                    components={constrainedComponents}
                                    title={null}
                                    disabled={isLoading}
                                    graph={graph}
                                    handleChange={this.handleComponentChange} />
                            </TitledPanel>
                        }
                    </form>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <CancelButton onClick={this.exitAddParameter} />
                    <PrimaryButton disabled={disabled} onClick={this.addParameter}>Add</PrimaryButton>
                </DialogActions>
            </Dialog>
        );
    }
}

AddParameterDialog.defaultProps = {
};

AddParameterDialog.propTypes = {
    name: PropTypes.string,
    disabled: PropTypes.bool,
    defaultLang: PropTypes.string.isRequired,
    projectType: PropTypes.string,
    classes: PropTypes.object,
    project: PropTypes.string.isRequired,
    existing: PropTypes.array,
    components: PropTypes.array,
    graph: PropTypes.string.isRequired,
    queryVars: PropTypes.array,
    onAddParameter: PropTypes.func.isRequired
};

export default withStyles(styles)(AddParameterDialog);